import React, { useEffect, useState } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TEST_NAME_PREFIX, stripTestNamePrefix } from '@/shared/merchantStatus';

// Duplicate Merchant (FIG-320 / REQ-038): the old "Clone as test" action, moved
// to the Settings tab. Creates "[TEST] <name> (copy)" as an isolated test clone
// and copies its variant mapping. The Test status and copying Deployed
// Consideration need UC-214 and UC-216; the copy shows as Test via its prefix.

interface DuplicateMerchantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchant: { id: string; name: string };
  onDuplicated: (cloneId: string) => void;
}

const copyName = (name: string) => `${TEST_NAME_PREFIX} ${stripTestNamePrefix(name)} (copy)`;

export const DuplicateMerchantDialog: React.FC<DuplicateMerchantDialogProps> = ({ open, onOpenChange, merchant, onDuplicated }) => {
  const { toast } = useToast();
  const [cutoffDate, setCutoffDate] = useState('');
  const [isCloning, setIsCloning] = useState(false);

  useEffect(() => {
    if (open) setCutoffDate('');
  }, [open]);

  const confirm = async () => {
    setIsCloning(true);
    try {
      const { data: sourceOrg, error: fetchError } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', merchant.id)
        .single();

      if (fetchError || !sourceOrg) throw new Error('Failed to fetch source organisation');

      // Copy every column except the identity and ownership ones.
      const { id, created_at, updated_at, user_id, ...orgFields } = sourceOrg as Record<string, unknown>;
      void id; void created_at; void updated_at; void user_id;

      const { data: clonedOrg, error: cloneError } = await supabase
        .from('organisations')
        .insert({
          ...orgFields,
          name: copyName(String(sourceOrg.name ?? merchant.name)),
          is_test_clone: true,
          source_organisation_id: sourceOrg.id,
          // FIG-345 (UC-226): the copy starts as Test, whatever the source's
          // Status. Onboarding stays in progress, as copies were 'draft' before.
          status: 'test',
          onboarding_state: 'in_progress',
          snapshot_table_created: false,
          financial_snapshot_table_created: false,
          ...(cutoffDate ? { data_cutoff_date: cutoffDate } : {}),
        } as never)
        .select()
        .single();

      if (cloneError || !clonedOrg) throw cloneError ?? new Error('Failed to create the copy');

      try {
        await invokeFunction(supabase, 'clone-organisation-mapping-v1', {
          body: {
            sourceOrganisationId: sourceOrg.id,
            cloneOrganisationId: clonedOrg.id,
            dataCutoffDate: cutoffDate || null,
          },
        });
      } catch (copyError) {
        let rolledBack = false;
        try {
          await invokeFunction(supabase, 'delete-organization', {
            body: { organizationId: clonedOrg.id, isDraft: true },
          });
          rolledBack = true;
        } catch (rollbackError) {
          console.error('Failed to roll back draft clone after mapping copy failure', rollbackError);
        }
        const copyMessage = copyError instanceof Error ? copyError.message : 'Failed to copy variant mapping';
        throw new Error(
          rolledBack
            ? `Failed to copy variant mapping onto the clone: ${copyMessage}`
            : `Failed to copy variant mapping and could not delete the draft clone: ${copyMessage}`,
        );
      }

      toast({
        title: 'Test copy created',
        description: `${copyName(merchant.name)} has been created. Reports will be written to isolated test tables.`,
      });
      onOpenChange(false);
      onDuplicated(clonedOrg.id);
    } catch (error) {
      console.error('Error cloning organisation:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to clone organisation',
        variant: 'destructive',
      });
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate merchant</DialogTitle>
          <DialogDescription>
            Create <strong>{copyName(merchant.name)}</strong> to run test scenarios. All settings and connections are
            copied, but reports are written to isolated test tables. The copy can be deleted when testing is complete.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label htmlFor="duplicate-cutoff-date">
            Data Cutoff Date <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="duplicate-cutoff-date"
            type="date"
            value={cutoffDate}
            onChange={(e) => setCutoffDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
          <p className="text-xs text-muted-foreground">
            Only products sold on or before this date will be recognised on the copy. This simulates how the system
            would have behaved at a specific point in time.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={confirm} disabled={isCloning} className="bg-emerald-600 hover:bg-emerald-700">
            {isCloning ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Duplicating...</>
            ) : (
              <><FlaskConical className="mr-2 h-4 w-4" />Duplicate</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateMerchantDialog;
